import React from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';
export type NotificationPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';

interface NotificationAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
}

export interface NotificationProps {
  /** Type of notification (determines color and icon) */
  type?: NotificationType;

  /** Main title/heading of the notification */
  title: string;

  /** Optional description text below the title */
  message?: string | React.ReactNode;

  /** Position of the notification on the screen */
  position?: NotificationPosition;

  /** Show close button */
  showClose?: boolean;

  /** Callback when close button is clicked */
  onClose?: () => void;

  /** Optional action buttons (up to 2 recommended) */
  actions?: NotificationAction[];

  /** Auto-dismiss after milliseconds (0 = no auto-dismiss) */
  autoDismiss?: number;

  /** Custom icon (overrides default type icon) */
  icon?: React.ReactNode;

  /** Optional avatar image URL (for message notifications) */
  avatarUrl?: string;
}

const typeConfig = {
  success: {
    bgColor: 'bg-panel',
    iconColor: 'text-green-400',
    Icon: CheckCircleIcon,
  },
  error: {
    bgColor: 'bg-panel',
    iconColor: 'text-red-400',
    Icon: XCircleIcon,
  },
  info: {
    bgColor: 'bg-panel',
    iconColor: 'text-blue-400',
    Icon: InformationCircleIcon,
  },
  warning: {
    bgColor: 'bg-panel',
    iconColor: 'text-yellow-400',
    Icon: ExclamationTriangleIcon,
  },
};

const positionClasses = {
  'top-right': 'top-0 right-0 items-end sm:items-end',
  'top-left': 'top-0 left-0 items-start sm:items-start',
  'bottom-right': 'bottom-0 right-0 items-end sm:items-end',
  'bottom-left': 'bottom-0 left-0 items-start sm:items-start',
  'top-center': 'items-center animate-drop-down',
  'bottom-center': 'bottom-0 left-1/2 -translate-x-1/2 items-center',
};

export const Notification: React.FC<NotificationProps> = ({
  type = 'info',
  title,
  message,
  position = 'top-right',
  showClose = true,
  onClose,
  actions = [],
  autoDismiss = 0,
  icon,
  avatarUrl,
}) => {
  const config = typeConfig[type];
  const IconComponent = config.Icon;

  // Auto-dismiss functionality
  React.useEffect(() => {
    if (autoDismiss > 0 && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoDismiss);
      return () => clearTimeout(timer);
    }
  }, [autoDismiss, onClose]);

  // Determine layout based on props
  const hasActions = actions.length > 0;
  const hasAvatar = !!avatarUrl;

  if (hasAvatar) {
    // Message-style notification with avatar
    return (
      <div className={`pointer-events-auto flex w-full max-w-md translate-y-0 transform ${
        hasActions ? 'divide-x divide-gray-200 dark:divide-gray-700' : ''
      } rounded-lg ${config.bgColor} opacity-100 shadow-lg ring-1 ring-black/5 transition duration-300 ease-out`}>
        <div className="w-0 flex-1 p-4">
          <div className="flex items-start">
            <div className="shrink-0 pt-0.5">
              <img
                src={avatarUrl}
                alt=""
                className="h-10 w-10 rounded-full bg-gray-100 dark:bg-gray-700 ring-1 ring-black/5"
              />
            </div>
            <div className="ml-3 w-0 flex-1">
              <p className="text-sm font-medium text-heading">{title}</p>
              {message && (
                <p className="mt-1 text-sm text-muted">{message}</p>
              )}
            </div>
          </div>
        </div>
        {hasActions && (
          <div className="flex">
            <button
              type="button"
              onClick={actions[0].onClick}
              className="flex w-full items-center justify-center rounded-none rounded-r-lg p-4 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 focus:outline-2 focus:outline-indigo-500"
            >
              {actions[0].label}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (hasActions && actions.length === 2) {
    // Two-action notification with split buttons
    return (
      <div className={`pointer-events-auto flex w-full max-w-md translate-y-0 transform divide-x divide-gray-200 dark:divide-gray-700 rounded-lg ${config.bgColor} opacity-100 shadow-lg ring-1 ring-black/5 transition duration-300 ease-out`}>
        <div className="flex w-0 flex-1 items-center p-4">
          <div className="w-full">
            <p className="text-sm font-medium text-heading">{title}</p>
            {message && (
              <p className="mt-1 text-sm text-muted">{message}</p>
            )}
          </div>
        </div>
        <div className="flex">
          <div className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700">
            <div className="flex h-0 flex-1">
              <button
                type="button"
                onClick={actions[0].onClick}
                className="flex w-full items-center justify-center rounded-none rounded-tr-lg px-4 py-3 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 focus:z-10 focus:outline-2 focus:outline-indigo-500"
              >
                {actions[0].label}
              </button>
            </div>
            <div className="flex h-0 flex-1">
              <button
                type="button"
                onClick={actions[1].onClick}
                className="flex w-full items-center justify-center rounded-none rounded-br-lg px-4 py-3 text-sm font-medium text-label hover:text-gray-500 dark:hover:text-gray-400 focus:outline-2 focus:outline-indigo-500"
              >
                {actions[1].label}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (hasActions && actions.length === 1) {
    // Single action notification with inline button
    return (
      <div className={`pointer-events-auto w-full max-w-sm translate-y-0 transform rounded-lg ${config.bgColor} opacity-100 shadow-lg ring-1 ring-black/5 transition duration-300 ease-out`}>
        <div className="p-4">
          <div className="flex items-center">
            <div className="flex w-0 flex-1 justify-between">
              <p className="w-0 flex-1 text-sm font-medium text-heading">{title}</p>
              <button
                type="button"
                onClick={actions[0].onClick}
                className="ml-3 shrink-0 rounded-md bg-panel text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 focus:outline-2 focus:outline-offset-2 focus:outline-indigo-500"
              >
                {actions[0].label}
              </button>
            </div>
            {showClose && onClose && (
              <div className="ml-4 flex shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (hasActions && actions.length > 2) {
    // Multiple actions notification with action buttons below
    return (
      <div className={`pointer-events-auto w-full max-w-sm translate-y-0 transform rounded-lg ${config.bgColor} opacity-100 shadow-lg ring-1 ring-black/5 transition duration-300 ease-out`}>
        <div className="p-4">
          <div className="flex items-start">
            <div className="shrink-0">
              {icon || <IconComponent className={`h-6 w-6 ${config.iconColor}`} />}
            </div>
            <div className="ml-3 w-0 flex-1 pt-0.5">
              <p className="text-sm font-medium text-heading">{title}</p>
              {message && (
                <p className="mt-1 text-sm text-muted">{message}</p>
              )}
              <div className="mt-3 flex space-x-7">
                {actions.map((action, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={action.onClick}
                    className={`rounded-md text-sm font-medium focus:outline-2 focus:outline-offset-2 focus:outline-indigo-500 ${
                      action.variant === 'primary' || index === 0
                        ? 'text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300'
                        : 'text-label hover:text-gray-500 dark:hover:text-gray-400'
                    }`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
            {showClose && onClose && (
              <div className="ml-4 flex shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default notification with icon and close button
  return (
    <div className={`pointer-events-auto w-full max-w-sm translate-y-0 transform rounded-lg ${config.bgColor} opacity-100 shadow-2xl ring-1 ring-black/10 transition duration-300 ease-out`} style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
      <div className="p-4">
        <div className="flex items-start">
          <div className="shrink-0">
            {icon || <IconComponent className={`h-6 w-6 ${config.iconColor}`} />}
          </div>
          <div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium text-heading">{title}</p>
            {message && (
              <p className="mt-1 text-sm text-muted">{message}</p>
            )}
          </div>
          {showClose && onClose && (
            <div className="ml-4 flex shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * NotificationContainer - Wrapper component for positioning notifications
 * Place this at the root of your app to render notifications
 */
interface NotificationContainerProps {
  children: React.ReactNode;
  position?: NotificationPosition;
}

export const NotificationContainer: React.FC<NotificationContainerProps> = ({
  children,
  position = 'top-right',
}) => {
  const positionClass = positionClasses[position];

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 flex px-4 py-6 sm:p-6 z-[9999]"
    >
      <div className={`flex w-full flex-col space-y-4 ${positionClass}`}>
        {children}
      </div>
    </div>
  );
};

export default Notification;
